import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';

import Formsy from 'formsy-react';
import { clone, debounce } from 'lodash';
import { Blend, Search, TrashIcon } from 'lucide-react';
import { Button, DefaultPopover, Separator, Input as InputPure, List as TWList, ListItem, DefaultTooltip, FormGroup, Label, ButtonIcon } from 'twyne-new.ui';

import { useAppAlert } from '@Components/AppAlert';
import Input from 'components/form/Input';

const propTypes = {
  store: PropTypes.object,
  configuration: PropTypes.object,
  onSelect: PropTypes.func,
  onApply: PropTypes.func,
}

export default function ReportViews({ store, configuration, onSelect, onApply }) {
  const { handleSuccess, handleError } = useAppAlert()
  const { useLoad, items, useSubmit, deleteEntry } = store

  const handleSubmit = useSubmit()

  const loadData = useLoad()

  const handleDelete = useCallback(async row => {
    await deleteEntry(row)
    await loadData()
  }, [loadData, deleteEntry])

  const handleValidSubmit = async ({ id, description }) => {
    const { filterby, groupby, sortby, metrics, pivotby } = onApply(clone(configuration))
    try {
      await handleSubmit({ id, description, configuration: { filterby, groupby, sortby, metrics, pivotby } })
      await loadData()
      handleSuccess('Success', "Report View created.");
    } catch (error) {
      handleError('Error', "Report View was not saved.");
    }
  }

  return <>
    <Save items={items} onSubmit={handleValidSubmit} />
    <Load items={items} onSelect={onSelect} onDelete={handleDelete} />
  </>
}

const Load = ({ items, onSelect, onDelete }) => {
  const [toggle, setToggle] = useState(false)
  const [value, setValue] = useState('')
  const [views, setViews] = useState([])
  const [filteredViews, setFilteredViews] = useState([])

  useEffect(() => {
    setViews(items)
    setFilteredViews(items)
  }, [items])

  const handleChange = event => {
    const { value } = event.target
    setValue(value)
    handleFilter(value)
  }

  const handleFilter = value => debounce(() => {
    const regex = RegExp(value, 'i')
    const filtered = views.filter(item => regex.test(item.description))
    setFilteredViews(filtered)
  }, 700)()

  const handleSelect = configuration => {
    onSelect(configuration)
    setToggle(false)
  }

  const handleDelete = item => {
    onDelete(item)
  }

  return <>
    <DefaultPopover
      trigger={<Button variant="outline" size='lg'>Load Settings</Button>}
      wrapperProps={{ toggle, onOpenChange: setToggle }}
    >
      <span className='twy-text-base-accent text-lg'>Select a report view</span>
      <Separator className='m-0 my-2' />
      <div className='relative w-full sticky top-0'>
        <InputPure
          placeholder='Search...'
          className='pl-8 border-0 focus:bg-gray-200 focus:dark:bg-gray-950'
          value={value}
          onChange={handleChange}
        />
        <Search size='18' className='twy-text-base absolute left-2 top-1/2 -translate-y-1/2' />
      </div>
      <Separator className='mt-2' />
      <List items={filteredViews} onDelete={handleDelete} onSelect={handleSelect} />
    </DefaultPopover>
  </>
}

const Save = ({ items, onSubmit }) => {
  const [toggle, setToggle] = useState(false)

  const handleSubmit = model => {
    setToggle(false)
    onSubmit(model)
  }

  return <>
    <DefaultPopover
      trigger={<Button color="success" size='lg' onClick={() => setToggle(true)}>Save</Button>}
      wrapperProps={{ toggle, onOpenChange: setToggle }}
    >
      <span className='twy-text-base text-lg'>Save report view</span>
      <Separator className='m-0 my-2' />
      <Formsy onValidSubmit={handleSubmit}>
        <FormGroup>
          <Label>Create a new report view</Label>
          <Input name='description' required />
        </FormGroup>
        <Button block type='submit' color="accent" className='mt-2'>Save</Button>
        <Separator className='my-2' />
        <Label>or override to a already saved report view</Label>
        <List items={items} onSave={handleSubmit} />
      </Formsy>
    </DefaultPopover>
  </>
}

const List = ({ items, onSelect, onSave, onDelete }) => {
  const props = onSelect ? { className: "cursor-pointer", action: true } : {}

  if (!items.length) {
    return <div className='text-center text-text-base'>No views yet.</div>
  }

  return <div className='max-h-[50vh] overflow-y-auto'>
    <TWList border className='px-0!'>
      {items.map(item => <ListItem {...props} key={item.id} onClick={() => onSelect && onSelect(item.configuration)}>
        <div className='flex justify-between w-full items-center'>
          {item.description}
          <div>
            {onSave && <>
              <DefaultTooltip content='Override'>
                <Button color='accent' size='sm' variant='ghost' onClick={() => onSave(item)}>
                  <Blend size='14' />
                </Button>
              </DefaultTooltip>
            </>}
            {onDelete && <>
              <ButtonIcon content='Delete' onClick={() => onDelete(item)}>
                <TrashIcon size='14' />
              </ButtonIcon>
            </>}
          </div>
        </div>
      </ListItem>)}
    </TWList>
  </div>
}

ReportViews.propTypes = propTypes
ReportViews.defaultProps = {}
